<template>
  <section
    class="panel"
    @mouseenter="pauseAutoScroll"
    @mouseleave="resumeAutoScroll"
  >
    <div class="panel-header">
      <h2>Server Monitor</h2>
      <div class="panel-meta">
        {{ displayServers.length }} servers
        <button
          v-if="sortKey !== 'rank' || sortDirection !== 'asc'"
          class="reset-sort-btn"
          @click="resetSort"
        >
          Reset sort
        </button>
      </div>
    </div>

    <div class="panel-body" ref="scrollContainer">
      <table class="security-table">
        <thead>
          <tr>
            <th @click="setSort('name')" class="sortable">
              Server {{ sortIndicator("name") }}
            </th>
            <th
              v-for="site in siteColumns"
              :key="site.key"
              @click="setSort(site.key)"
              class="sortable site-col"
            >
              {{ site.label }} {{ sortIndicator(site.key) }}
            </th>
            <th @click="setSort('load')" class="sortable">
              Load {{ sortIndicator("load") }}
              <span class="info-wrap" @click.stop>
                ⓘ
                <span class="info-tooltip">
                  Green: &lt; 2<br />
                  Orange: 2–4<br />
                  Red: ≥ 4
                </span>
              </span>
            </th>
            <th @click="setSort('mem')" class="sortable">
              Memory {{ sortIndicator("mem") }}
              <span class="info-wrap" @click.stop>
                ⓘ
                <span class="info-tooltip">
                  Green: &lt; 80%<br />
                  Orange: 80–90%<br />
                  Red: ≥ 90%
                </span>
              </span>
            </th>
            <th @click="setSort('disk')" class="sortable">
              Disk {{ sortIndicator("disk") }}
              <span class="info-wrap" @click.stop>
                ⓘ
                <span class="info-tooltip">
                  Green: &lt; 85%<br />
                  Orange: 85–95%<br />
                  Red: ≥ 95%
                </span>
              </span>
            </th>
          </tr>
        </thead>

        <tbody>
          <tr
            v-for="server in displayServers"
            :key="server.name"
            class="table-row"
          >
            <td class="server-name">
              <span class="server-name__wrap">
                <span
                  class="status-dot"
                  :class="statusClass(server)"
                ></span>
                <span>{{ server.name }}</span>
                <span class="connection-tooltip">
                  {{ connectionTitle(server) }}
                </span>
              </span>
            </td>

            <td
              v-for="site in siteColumns"
              :key="site.key"
              class="site-cell"
            >
              <span
                class="site-status"
                :class="siteStatusClass(server, site.key)"
              >
                <span class="site-status__dot"></span>
                <span>{{ siteStatusLabel(server, site.key) }}</span>
                <span class="site-tooltip">
                  {{ siteStatusTitle(server, site.key) }}
                </span>
              </span>
            </td>

            <td>
              <div class="table-metric">
                <div class="table-metric__value">
                  {{ loadLabel(server) }}
                </div>
                <div class="table-metric__bar">
                  <div
                    class="table-metric__fill"
                    :class="metricBarClass(server, 'load')"
                    :style="{ width: loadBarWidth(server) }"
                  ></div>
                </div>
              </div>
            </td>

            <td>
              <div class="table-metric">
                <div class="table-metric__value">
                  {{ memLabel(server) }}
                </div>
                <div class="table-metric__bar">
                  <div
                    class="table-metric__fill"
                    :class="metricBarClass(server, 'mem')"
                    :style="{ width: memBarWidth(server) }"
                  ></div>
                </div>
              </div>
            </td>

            <td>
              <div class="table-metric">
                <div class="table-metric__value">
                  {{ diskLabel(server) }}
                </div>
                <div class="table-metric__bar">
                  <div
                    class="table-metric__fill"
                    :class="metricBarClass(server, 'disk')"
                    :style="{ width: diskBarWidth(server) }"
                  ></div>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </section>
</template>

<script>
import axios from "axios";
import { setMonitorAlerts } from "@/store/monitorAlerts";

export default {
  props: {
    searchQuery: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      servers: [],
      refreshInterval: null,
      sortKey: "rank",
      sortDirection: "asc",
      autoScrollInterval: null,
      autoScrollPaused: false,
      autoScrollStep: 1,
      autoScrollDelay: 40,
      isRefreshing: false,
      siteColumns: [
        { key: "api", label: "API" },
        { key: "dashboard", label: "Dash" },
        { key: "staging", label: "Staging" },
        { key: "prod", label: "Prod" },
        { key: "favourites", label: "Favs" },
      ],
    };
  },

  computed: {
    normalizedSearch() {
      return this.searchQuery.trim().toLowerCase();
    },

    filteredServers() {
      if (!this.normalizedSearch) return this.servers || [];

      return (this.servers || []).filter(server =>
        String(server.name || "")
          .toLowerCase()
          .includes(this.normalizedSearch)
      );
    },

    displayServers() {
      const servers = this.filteredServers.slice();
      const direction = this.sortDirection === "asc" ? 1 : -1;

      return servers.sort((a, b) => {
        let valueA;
        let valueB;

        if (this.isSiteSortKey(this.sortKey)) {
          return this.compareSiteStatus(a, b, this.sortKey, direction);
        }

        switch (this.sortKey) {
          case "name":
            valueA = a.name || "";
            valueB = b.name || "";
            return valueA.localeCompare(valueB) * direction;

          case "status":
            valueA = this.serverRank(a);
            valueB = this.serverRank(b);
            if (valueA !== valueB) return (valueA - valueB) * direction;
            return a.name.localeCompare(b.name);

          case "uptime":
            valueA = this.uptimeValue(a);
            valueB = this.uptimeValue(b);
            break;

          case "load":
            valueA = this.loadValue(a);
            valueB = this.loadValue(b);
            break;

          case "mem":
            valueA = this.memPct(a);
            valueB = this.memPct(b);
            break;

          case "disk":
            valueA = this.diskPct(a);
            valueB = this.diskPct(b);
            break;

          case "rank":
          default:
            valueA = this.serverRank(a);
            valueB = this.serverRank(b);
            if (valueA !== valueB) return (valueA - valueB) * direction;
            return a.name.localeCompare(b.name);
        }

        const aMissing = valueA === null || valueA === undefined;
        const bMissing = valueB === null || valueB === undefined;

        if (aMissing && bMissing) return a.name.localeCompare(b.name);
        if (aMissing) return 1;
        if (bMissing) return -1;

        if (valueA < valueB) return -1 * direction;
        if (valueA > valueB) return 1 * direction;

        return a.name.localeCompare(b.name);
      });
    },

    summary() {
      const total = (this.servers || []).length;

      let ok = 0;
      let notDeployed = 0;
      let unreachable = 0;
      let unauthorized = 0;
      let otherErrors = 0;

      (this.servers || []).forEach(server => {
        const category = this.statusCategory(server);

        if (category === "ok") ok++;
        else if (category === "not_deployed") notDeployed++;
        else if (category === "unreachable") unreachable++;
        else if (category === "unauthorized") unauthorized++;
        else otherErrors++;
      });

      return {
        total,
        ok,
        notDeployed,
        unreachable,
        unauthorized,
        otherErrors,
      };
    },
  },

  mounted() {
    this.refresh();

    this.refreshInterval = setInterval(() => {
      this.refresh();
    }, 30000);

    this.startAutoScroll();
  },

  beforeDestroy() {
    if (this.refreshInterval) clearInterval(this.refreshInterval);
    if (this.autoScrollInterval) clearInterval(this.autoScrollInterval);
  },

  methods: {
    async refresh() {
      if (this.isRefreshing) return;

      this.isRefreshing = true;

      try {
        const response = await axios.get("/api/security", {
          timeout: 10000,
        });

        const payload = response.data || {};
        const incomingServers = payload.servers || [];

        this.servers = incomingServers.map(server => ({
          ...server,
          alerts: server.alerts !== false,
        }));

        this.publishSecurityAlerts();
      } catch (error) {
        console.error("Failed to fetch /api/security", error);
      } finally {
        this.isRefreshing = false;
      }
    },

    startAutoScroll() {
      if (this.autoScrollInterval) clearInterval(this.autoScrollInterval);

      this.autoScrollInterval = setInterval(() => {
        if (this.autoScrollPaused) return;

        const el = this.$refs.scrollContainer;
        if (!el) return;

        const bottom = el.scrollHeight - el.clientHeight;
        if (bottom <= 0) return;

        const nextScrollTop = el.scrollTop + this.autoScrollStep;
        const lastRow = el.querySelector("tbody tr:last-child");
        const threshold = lastRow
          ? Math.max(Math.floor(lastRow.offsetHeight / 2), 24)
          : 24;

        if (nextScrollTop >= bottom - threshold) {
          el.scrollTop = 0;
        } else {
          el.scrollTop = nextScrollTop;
        }
      }, this.autoScrollDelay);
    },

    pauseAutoScroll() {
      this.autoScrollPaused = true;
    },

    resumeAutoScroll() {
      this.autoScrollPaused = false;
    },

    system(server) {
      return server && server.data && server.data.system
        ? server.data.system
        : null;
    },

    statusCategory(server) {
      if (!server || !server.error) return "ok";

      const error = String(server.error || "").toLowerCase();

      if (error.includes("http 404")) return "not_deployed";
      if (error.includes("http 401") || error.includes("http 403")) {
        return "unauthorized";
      }

      if (
        error.includes("timeout") ||
        error.includes("econnrefused") ||
        error.includes("enotfound") ||
        error.includes("temporary failure in name resolution") ||
        error.includes("network error")
      ) {
        return "unreachable";
      }

      return "other_error";
    },

    statusClass(server) {
      const category = this.statusCategory(server);

      if (category === "ok") return "online";
      if (category === "not_deployed") return "warning";
      return "offline";
    },

    statusLabel(server) {
      if (!server.error) return "OK";
      return server.error;
    },

    connectionTitle(server) {
      return `Connection: ${this.statusLabel(server)}`;
    },

    siteStatus(server, key) {
      return server && server.kuma && server.kuma[key]
        ? server.kuma[key]
        : null;
    },

    siteStatusCategory(server, key) {
      if (server && server.kuma && server.kuma.unavailable) {
        return "unavailable";
      }

      const monitor = this.siteStatus(server, key);
      return monitor && monitor.status ? monitor.status : "missing";
    },

    siteStatusClass(server, key) {
      const category = this.siteStatusCategory(server, key);

      if (category === "up") return "is-up";
      if (category === "maintenance") return "is-maintenance";
      if (category === "pending") return "is-pending";
      if (category === "down") return "is-down";
      return "is-missing";
    },

    siteStatusLabel(server, key) {
      const monitor = this.siteStatus(server, key);
      const category = this.siteStatusCategory(server, key);

      if (category === "up") {
        return monitor && typeof monitor.ping === "number"
          ? `${Math.round(monitor.ping)}ms`
          : "OK";
      }

      if (category === "down") return "DOWN";
      if (category === "pending") return "WAIT";
      if (category === "maintenance") return "MAINT";
      if (category === "unavailable") return "N/A";
      return "-";
    },

    siteStatusTitle(server, key) {
      const monitor = this.siteStatus(server, key);
      const label = this.siteColumns.find(site => site.key === key);
      const category = this.siteStatusCategory(server, key);

      if (category === "unavailable") {
        const reason =
          server && server.kuma && server.kuma.error
            ? server.kuma.error
            : "Kuma data unavailable";

        return `${label ? label.label : key}: ${reason}`;
      }

      if (!monitor || monitor.status === "missing") {
        return `${label ? label.label : key}: monitor not found in Kuma status page`;
      }

      const details = [
        monitor.name || (label ? label.label : key),
        `Status: ${monitor.status}`,
      ];

      if (typeof monitor.ping === "number") {
        details.push(`Ping: ${Math.round(monitor.ping)}ms`);
      }

      if (typeof monitor.uptime24 === "number") {
        details.push(`24h uptime: ${(monitor.uptime24 * 100).toFixed(2)}%`);
      }

      if (monitor.updatedAt) details.push(`Updated: ${monitor.updatedAt}`);
      if (monitor.url) details.push(monitor.url);

      return details.join("\n");
    },

    loadLabel(server) {
      const system = this.system(server);
      const load = system && system.load ? system.load : null;

      if (!load) return "-";

      const format = value =>
        typeof value === "number" ? value.toFixed(2) : "-";

      return `${format(load["1m"])}/${format(load["5m"])}/${format(
        load["15m"]
      )}`;
    },

    memLabel(server) {
      const system = this.system(server);
      const memory = system && system.mem_mb ? system.mem_mb : null;

      if (
        !memory ||
        typeof memory.total !== "number" ||
        typeof memory.used !== "number" ||
        memory.total <= 0
      ) {
        return "-";
      }

      const percent = Math.round((memory.used / memory.total) * 100);
      return `${percent}%`;
    },

    diskLabel(server) {
      const system = this.system(server);
      const disk = system && system.disk_root ? system.disk_root : null;

      if (
        !disk ||
        typeof disk.used_mb !== "number" ||
        typeof disk.total_mb !== "number" ||
        disk.total_mb <= 0
      ) {
        return "-";
      }

      if (typeof disk.use_pct === "string") return disk.use_pct;

      const percent = Math.round((disk.used_mb / disk.total_mb) * 100);
      return `${percent}%`;
    },

    uptimeLabel(server) {
      const system = this.system(server);
      const uptime = system && system.uptime_s;

      if (typeof uptime !== "number" || !isFinite(uptime)) return "-";

      const seconds = Math.floor(uptime);
      const days = Math.floor(seconds / 86400);
      const hours = Math.floor((seconds % 86400) / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);

      if (days > 0) return `${days}d ${hours}h`;
      if (hours > 0) return `${hours}h ${minutes}m`;
      return `${minutes}m`;
    },

    memPct(server) {
      const system = this.system(server);
      const memory = system && system.mem_mb ? system.mem_mb : null;

      if (
        !memory ||
        typeof memory.used !== "number" ||
        typeof memory.total !== "number" ||
        memory.total <= 0
      ) {
        return null;
      }

      return (memory.used / memory.total) * 100;
    },

    diskPct(server) {
      const system = this.system(server);
      const disk = system && system.disk_root ? system.disk_root : null;

      if (!disk) return null;

      if (typeof disk.use_pct === "string" && disk.use_pct.endsWith("%")) {
        const value = Number(disk.use_pct.replace("%", ""));
        return Number.isFinite(value) ? value : null;
      }

      if (
        typeof disk.used_mb === "number" &&
        typeof disk.total_mb === "number" &&
        disk.total_mb > 0
      ) {
        return (disk.used_mb / disk.total_mb) * 100;
      }

      return null;
    },

    loadValue(server) {
      const system = this.system(server);
      const load = system && system.load ? system.load : null;
      const oneMinute = load ? load["1m"] : null;

      return typeof oneMinute === "number" ? oneMinute : null;
    },

    serverRank(server) {
      const category = this.statusCategory(server);

      if (category === "unreachable") return 0;
      if (category === "unauthorized") return 1;
      if (category === "not_deployed") return 2;
      if (category === "other_error") return 3;
      if (this.hasKumaDown(server)) return 4;

      const disk = this.diskPct(server);
      const mem = this.memPct(server);

      if (disk !== null && disk >= 95) return 5;
      if (mem !== null && mem >= 90) return 6;
      if (disk !== null && disk >= 85) return 7;
      if (mem !== null && mem >= 80) return 8;

      return 10;
    },

    isSiteSortKey(key) {
      return this.siteColumns.some(site => site.key === key);
    },

    siteSortValue(server, key) {
      const category = this.siteStatusCategory(server, key);

      if (category === "down") return 0;
      if (category === "pending") return 1;
      if (category === "maintenance") return 2;
      if (category === "unknown") return 3;
      if (category === "missing") return 4;
      if (category === "unavailable") return 5;
      return 10;
    },

    sitePingValue(server, key) {
      const monitor = this.siteStatus(server, key);
      return monitor && typeof monitor.ping === "number" ? monitor.ping : null;
    },

    compareSiteStatus(a, b, key, direction) {
      const valueA = this.siteSortValue(a, key);
      const valueB = this.siteSortValue(b, key);

      if (valueA !== valueB) return (valueA - valueB) * direction;

      const pingA = this.sitePingValue(a, key);
      const pingB = this.sitePingValue(b, key);
      const aMissing = pingA === null || pingA === undefined;
      const bMissing = pingB === null || pingB === undefined;

      if (!aMissing && !bMissing && pingA !== pingB) {
        return (pingA - pingB) * direction;
      }

      if (aMissing && !bMissing) return 1;
      if (!aMissing && bMissing) return -1;

      return a.name.localeCompare(b.name);
    },

    hasKumaDown(server) {
      return this.siteColumns.some(site => {
        return this.siteStatusCategory(server, site.key) === "down";
      });
    },

    hasKumaApiDown(server) {
      return this.siteStatusCategory(server, "api") === "down";
    },

    metricBarClass(server, metric) {
      const severity = this.metricClass(server, metric);
      if (severity === "metric-bad") return "is-bad";
      if (severity === "metric-warn") return "is-warn";
      return "is-ok";
    },

    metricClass(server, metric) {
      if (server && server.error) return "";

      if (metric === "disk") {
        const value = this.diskPct(server);
        if (value === null) return "";
        if (value >= 95) return "metric-bad";
        if (value >= 85) return "metric-warn";
        return "metric-ok";
      }

      if (metric === "mem") {
        const value = this.memPct(server);
        if (value === null) return "";
        if (value >= 90) return "metric-bad";
        if (value >= 80) return "metric-warn";
        return "metric-ok";
      }

      if (metric === "load") {
        const value = this.loadValue(server);
        if (value === null) return "";
        if (value >= 4) return "metric-bad";
        if (value >= 2) return "metric-warn";
        return "metric-ok";
      }

      return "";
    },

    clampPercent(value) {
      if (typeof value !== "number" || !isFinite(value)) return "0%";
      const clamped = Math.max(0, Math.min(100, value));
      return `${clamped}%`;
    },

    loadBarWidth(server) {
      const value = this.loadValue(server);
      if (value === null) return "0%";
      return this.clampPercent((value / 4) * 100);
    },

    memBarWidth(server) {
      return this.clampPercent(this.memPct(server));
    },

    diskBarWidth(server) {
      return this.clampPercent(this.diskPct(server));
    },

    publishSecurityAlerts() {
      const alertableServers = (this.servers || []).filter(
        server => server.alerts !== false
      );

      const apisDown = alertableServers.filter(server => {
        return this.hasKumaApiDown(server);
      }).length;

      const highDisk = alertableServers.filter(server => {
        if (server.error) return false;
        const disk = this.diskPct(server);
        return disk !== null && disk >= 85;
      }).length;

      setMonitorAlerts({
        apisDown,
        serversDown: 0,
        highDisk,
      });
    },

    setSort(key) {
      if (this.sortKey === key) {
        this.sortDirection = this.sortDirection === "asc" ? "desc" : "asc";
      } else {
        this.sortKey = key;

        if (key === "name") {
          this.sortDirection = "asc";
        } else if (
          key === "uptime" ||
          key === "load" ||
          key === "mem" ||
          key === "disk"
        ) {
          this.sortDirection = "desc";
        } else {
          this.sortDirection = "asc";
        }
      }
    },

    sortIndicator(key) {
      if (this.sortKey !== key) return "↕";
      return this.sortDirection === "asc" ? "↑" : "↓";
    },

    uptimeValue(server) {
      const system = this.system(server);
      const uptime = system && system.uptime_s;
      return typeof uptime === "number" && isFinite(uptime) ? uptime : null;
    },

    resetSort() {
      this.sortKey = "rank";
      this.sortDirection = "asc";
    },
  },
};
</script>

<style scoped>
.panel {
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #0b1622;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
}

.panel-header {
  flex-shrink: 0;
  padding: 14px 16px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.panel-header h2 {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
  color: #f1f5f9;
}

.panel-meta {
  display: flex;
  align-items: center;
  gap: 10px;
  color: #94a3b8;
  font-size: 0.82rem;
}

.panel-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
}

.panel-body::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

.panel-body::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.14);
  border-radius: 999px;
}

.table-wrap::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.14);
  border-radius: 999px;
}

.security-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
}

.security-table th {
  text-align: left;
  padding: 10px 8px;
  color: #94a3b8;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  position: sticky;
  top: 0;
  background: #0b1622;
  z-index: 1;
  white-space: nowrap;
}

.security-table td {
  padding: 10px 8px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.03);
  color: #e2e8f0;
  white-space: nowrap;
  vertical-align: middle;
}

.table-row {
  transition: background 0.15s ease;
}

.table-row:hover {
  background: rgba(255, 255, 255, 0.03);
}

.server-name {
  font-weight: 600;
  color: #f8fafc;
}

.server-name__wrap {
  position: relative;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.server-name__wrap:hover .connection-tooltip {
  opacity: 1;
  transform: translateY(0);
}

.connection-tooltip {
  position: absolute;
  left: 0;
  top: calc(100% + 8px);
  z-index: 20;
  max-width: 260px;
  padding: 7px 9px;
  border-radius: 6px;
  background: #020617;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #e2e8f0;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.35;
  white-space: normal;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.42);
  opacity: 0;
  pointer-events: none;
  transform: translateY(-2px);
  transition: opacity 0s linear, transform 0s linear;
}

.status-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  display: inline-block;
  flex-shrink: 0;
}

.status-dot.online {
  background: #22c55e;
}

.status-dot.warning {
  background: #f59e0b;
}

.status-dot.offline {
  background: #ef4444;
}

.site-col,
.site-cell {
  min-width: 70px;
}

.site-status {
  position: relative;
  min-width: 62px;
  display: inline-flex;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  color: #cbd5e1;
  font-size: 11px;
  line-height: 1;
}

.site-status:hover .site-tooltip {
  opacity: 1;
  transform: translateY(0);
}

.site-tooltip {
  position: absolute;
  left: 0;
  top: calc(100% + 8px);
  z-index: 20;
  width: max-content;
  max-width: 280px;
  padding: 7px 9px;
  border-radius: 6px;
  background: #020617;
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #e2e8f0;
  font-size: 11px;
  font-weight: 500;
  line-height: 1.35;
  white-space: pre-line;
  box-shadow: 0 10px 28px rgba(0, 0, 0, 0.42);
  opacity: 0;
  pointer-events: none;
  transform: translateY(-2px);
  transition: opacity 0s linear, transform 0s linear;
}

.site-status__dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  flex-shrink: 0;
  background: #64748b;
}

.site-status.is-up .site-status__dot {
  background: #22c55e;
}

.site-status.is-down .site-status__dot {
  background: #ef4444;
}

.site-status.is-pending .site-status__dot,
.site-status.is-maintenance .site-status__dot {
  background: #f59e0b;
}

.site-status.is-missing {
  color: #64748b;
}

.table-metric {
  min-width: 128px;
}

.table-metric__value {
  color: #e2e8f0;
  font-size: 12px;
  line-height: 1.2;
}

.table-metric__bar {
  margin-top: 6px;
  height: 6px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  overflow: hidden;
}

.table-metric__fill {
  height: 100%;
  border-radius: 999px;
}

.table-metric__fill.is-ok {
  background: #22c55e;
}

.table-metric__fill.is-warn {
  background: #f59e0b;
}

.table-metric__fill.is-bad {
  background: #ef4444;
}

.sortable {
  cursor: pointer;
  user-select: none;
  transition: color 0.15s ease;
}

.sortable:hover {
  color: #e2e8f0;
}

.reset-sort-btn {
  padding: 4px 8px;
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  background: rgba(255, 255, 255, 0.04);
  color: #cbd5e1;
  font-size: 11px;
  cursor: pointer;
  transition: 0.15s ease;
}

.reset-sort-btn:hover {
  background: rgba(255, 255, 255, 0.08);
  color: #f8fafc;
}

.info-wrap {
  position: relative;
  margin-left: 6px;
  font-size: 11px;
  color: #64748b;
  cursor: default;
}

.info-wrap:hover .info-tooltip {
  opacity: 1;
  transform: translateY(0);
  pointer-events: auto;
}

.info-tooltip {
  position: absolute;
  top: 18px;
  left: 0;
  z-index: 10;
  min-width: 140px;
  padding: 8px 10px;
  border-radius: 8px;
  background: #020617;
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #e2e8f0;
  font-size: 11px;
  line-height: 1.4;
  opacity: 0;
  transform: translateY(-4px);
  pointer-events: none;
  transition: 0.15s ease;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.4);
}
</style>
